import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Box,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import type { DashboardSidePanelConfig, DashboardLayoutMode, SidePanelAnchor } from './types';

interface DashboardSettingsDialogProps {
  open: boolean;
  sidePanel?: DashboardSidePanelConfig;
  layoutMode?: DashboardLayoutMode;
  onClose: () => void;
  onSave: (settings: {
    sidePanel?: DashboardSidePanelConfig;
    layoutMode?: DashboardLayoutMode;
  }) => void;
}

const DEFAULT_PANEL: DashboardSidePanelConfig = {
  enabled: false,
  childWidgetIds: [],
  anchor: 'right',
  panelWidth: 340,
};

const DashboardSettingsDialog: React.FC<DashboardSettingsDialogProps> = ({
  open,
  sidePanel: initialPanel,
  layoutMode: initialLayout,
  onClose,
  onSave,
}) => {
  const [panelEnabled, setPanelEnabled] = useState(false);
  const [anchor, setAnchor] = useState<SidePanelAnchor>('right');
  const [panelSize, setPanelSize] = useState(340);
  const [layoutMode, setLayoutMode] = useState<DashboardLayoutMode>('scroll');

  useEffect(() => {
    if (!open) return;
    const sp = initialPanel ?? DEFAULT_PANEL;
    setPanelEnabled(sp.enabled);
    setAnchor(sp.anchor);
    setPanelSize(sp.panelWidth);
    setLayoutMode(initialLayout ?? 'scroll');
  }, [open, initialPanel, initialLayout]);

  const handleSave = () => {
    const sp: DashboardSidePanelConfig = {
      enabled: panelEnabled,
      childWidgetIds: initialPanel?.childWidgetIds ?? [],
      anchor,
      panelWidth: panelSize,
    };
    onSave({ sidePanel: sp, layoutMode });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Dashboard Settings</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          {/* Layout mode */}
          <Box>
            <Typography variant="overline" color="text.secondary">Layout</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Controls how the dashboard grid fills available space.
            </Typography>
            <ToggleButtonGroup
              value={layoutMode}
              exclusive
              onChange={(_, v) => v && setLayoutMode(v)}
              size="small"
              fullWidth
            >
              <ToggleButton value="fit">Fit to screen</ToggleButton>
              <ToggleButton value="scroll">Scrollable</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Side panel */}
          <Box>
            <Typography variant="overline" color="text.secondary">Side Panel</Typography>
            <Divider sx={{ mb: 1.5 }} />
            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={panelEnabled}
                    onChange={(_, v) => setPanelEnabled(v)}
                  />
                }
                label="Enable side panel"
              />

              {panelEnabled && (
                <>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Dock position
                    </Typography>
                    <ToggleButtonGroup
                      value={anchor}
                      exclusive
                      onChange={(_, v) => v && setAnchor(v)}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value="left">Left</ToggleButton>
                      <ToggleButton value="right">Right</ToggleButton>
                      <ToggleButton value="bottom">Bottom</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {anchor === 'bottom' ? 'Panel height' : 'Panel width'} ({panelSize}px)
                    </Typography>
                    <Slider
                      value={panelSize}
                      onChange={(_, v) => setPanelSize(v as number)}
                      min={120}
                      max={anchor === 'bottom' ? 800 : 1200}
                      step={10}
                      valueLabelDisplay="auto"
                    />
                    <TextField
                      size="small"
                      type="number"
                      label="Exact size (px)"
                      value={panelSize}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (Number.isFinite(n) && n >= 80) setPanelSize(Math.min(n, 2000));
                      }}
                      inputProps={{ min: 80, max: 2000, step: 10 }}
                      sx={{ mt: 1, width: 160 }}
                    />
                  </Box>
                </>
              )}
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DashboardSettingsDialog;
